import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import {
  ExitFullscreenIcon,
  FullscreenIcon,
  PauseIcon,
  PipIcon,
  PlayIcon,
  SkipBackIcon,
  SkipForwardIcon,
  TheaterIcon,
  VolumeIcon,
  VolumeOffIcon,
} from '../components/icons';
import { Banner, cx } from '../components/ui';
import { timecode } from '../lib/format';
import type { Playback, Reaction } from '../lib/types';
import type HlsType from 'hls.js';
import { useHotkeys } from '../lib/useHotkeys';
import { ReactionMarkers } from './ReactionMarkers';
import { ReactionPops } from './ReactionPops';

/**
 * Playback.
 *
 * Safari (and every iOS browser, which is Safari underneath) plays HLS
 * natively and does it better than any JavaScript can, so it gets the URL
 * directly. Everyone else gets hls.js. If neither works we fall back to the
 * progressive MP4, which the pipeline always produces.
 *
 * The controls are custom rather than native because playback speed is a
 * first-class control for this product (people watch their colleagues'
 * updates at 1.5×), and because a share page is a piece of Slate's surface,
 * not a browser default.
 */

/** Shared with the audio player: one set of speeds across the product. */
export const SPEEDS = [1, 1.25, 1.5, 1.75, 2] as const;

export interface PlayerProps {
  playback: Playback;
  title: string;
  videoRef: RefObject<HTMLVideoElement | null>;
  /** Fires roughly every 250ms while playing. */
  onProgress?: (currentTime: number, duration: number) => void;
  onEnded?: () => void;
  /**
   * Timecoded reactions to draw on the scrub bar and surface as the playhead
   * reaches them. Omitted (or empty) and the player behaves exactly as before.
   */
  reactions?: readonly Reaction[];
  /** Captions showing from the start, as the owner asked. */
  captionsDefault?: boolean;
  /** The speed the page opens at, as the owner asked. */
  initialRate?: number;
  /**
   * Theater mode is the page's to lay out, so the player only reports the
   * press. Omitted and the button does not appear.
   */
  theater?: boolean;
  onTheaterToggle?: () => void;
  /** The CC button, with the state it moved to. */
  onCaptionsToggle?: (on: boolean) => void;
  /** The speed control, with the speed chosen. */
  onSpeedChange?: (speed: number) => void;
}

const NO_REACTIONS: readonly Reaction[] = [];

export function Player({
  playback,
  title,
  videoRef,
  onProgress,
  onEnded,
  reactions = NO_REACTIONS,
  captionsDefault = false,
  initialRate = 1,
  theater = false,
  onTheaterToggle,
  onCaptionsToggle,
  onSpeedChange,
}: PlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [rate, setRate] = useState(1);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  const [pip, setPip] = useState(false);
  // Captions start off: the transcript is under the video for anyone who
  // wants the words, and burned-in text over a screen recording hides the
  // thing being shown. One click brings them up.
  const [captions, setCaptions] = useState(captionsDefault);
  const toggleCaptions = () => {
    const video = videoRef.current;
    if (!video) return;
    const next = !captions;
    for (const track of Array.from(video.textTracks)) {
      track.mode = next ? 'showing' : 'hidden';
    }
    setCaptions(next);
    onCaptionsToggle?.(next);
  };
  const [error, setError] = useState<string | null>(null);
  const [showControls, setShowControls] = useState(true);
  const hideTimerRef = useRef<number | null>(null);
  // Play pressed before the source was attached. The moment the player
  // appears is exactly when someone who has been watching the processing
  // spinner presses play, and hls.js is still on its way over the network
  // then. The browser answers that play() with AbortError once the source
  // lands, so the wish is kept here and granted on attach instead.
  const wantPlay = useRef(false);

  // ---- owner's defaults -----------------------------------------------------
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const apply = () => {
      video.playbackRate = initialRate;
      for (const track of Array.from(video.textTracks)) {
        track.mode = captionsDefault ? 'showing' : 'hidden';
      }
    };
    video.addEventListener('loadedmetadata', apply);
    return () => video.removeEventListener('loadedmetadata', apply);
    // Only the first load: a viewer who changes the speed keeps their choice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoRef]);

  // ---- source ---------------------------------------------------------------
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    setError(null);

    const { hlsUrl, mp4Url } = playback;

    // A source swap mid watch (the original file giving way to the ladder)
    // keeps the playhead and whether it was playing. On first load both are
    // zero and false, so this costs nothing.
    const resumeAt = video.currentTime;
    const wasPlaying = !video.paused && !video.ended;
    if (resumeAt > 0.5) {
      const restore = () => {
        video.currentTime = resumeAt;
        if (wasPlaying) void video.play().catch(() => {});
      };
      video.addEventListener('loadedmetadata', restore, { once: true });
    }

    if (hlsUrl) {
      // hls.js is ~590kB (a third of the bundle) and only this component
      // ever needs it. Loading it here keeps it off the recorder and dashboard,
      // which are the pages people open most and where it would never be used.
      //
      // hls.js first, native HLS second. Chrome on macOS has started
      // answering "maybe" to canPlayType for HLS and then failing to demux
      // it, so the native path is for browsers where MSE is not available,
      // which is Safari on iOS and where it does actually work.
      let instance: HlsType | null = null;
      let cancelled = false;

      void (async () => {
        const { default: Hls } = await import('hls.js');
        if (cancelled) return;

        if (!Hls.isSupported()) {
          if (video.canPlayType('application/vnd.apple.mpegurl')) video.src = hlsUrl;
          else if (mp4Url) video.src = mp4Url;
          else setError('This browser cannot play this video.');
          return;
        }

        instance = new Hls({ enableWorker: true, backBufferLength: 60 });
        instance.loadSource(hlsUrl);
        instance.attachMedia(video);
        instance.on(Hls.Events.MEDIA_ATTACHED, () => {
          if (wantPlay.current) void video.play().catch(() => {});
        });
        instance.on(Hls.Events.ERROR, (_event, data) => {
          if (!data.fatal || !instance) return;
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            instance.startLoad();
          } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            instance.recoverMediaError();
          } else if (mp4Url) {
            instance.destroy();
            instance = null;
            video.src = mp4Url;
          } else {
            instance.destroy();
            instance = null;
            setError('This video would not play. Try reloading the page.');
          }
        });
      })();

      return () => {
        cancelled = true;
        instance?.destroy();
      };
    }

    if (mp4Url) {
      video.src = mp4Url;
      return;
    }

    setError('There is no playable version of this video yet.');
    return;
  }, [playback, videoRef]);

  // ---- element events -------------------------------------------------------
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onLoaded = () => setDuration(Number.isFinite(video.duration) ? video.duration : 0);
    const onTime = () => {
      setCurrentTime(video.currentTime);
      onProgress?.(video.currentTime, video.duration);
      if (video.buffered.length > 0) {
        setBuffered(video.buffered.end(video.buffered.length - 1));
      }
    };
    const onPlay = () => {
      wantPlay.current = false;
      setPlaying(true);
      setError(null);
    };
    const onPause = () => setPlaying(false);
    const onRate = () => setRate(video.playbackRate);
    const onVolume = () => {
      setMuted(video.muted);
      setVolume(video.volume);
    };
    const onEnd = () => {
      setPlaying(false);
      onEnded?.();
    };
    const onFail = () =>
      setError('This video would not play. It may still be processing. Try reloading.');

    video.addEventListener('loadedmetadata', onLoaded);
    video.addEventListener('durationchange', onLoaded);
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('ratechange', onRate);
    video.addEventListener('volumechange', onVolume);
    video.addEventListener('ended', onEnd);
    video.addEventListener('error', onFail);

    return () => {
      video.removeEventListener('loadedmetadata', onLoaded);
      video.removeEventListener('durationchange', onLoaded);
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ratechange', onRate);
      video.removeEventListener('volumechange', onVolume);
      video.removeEventListener('ended', onEnd);
      video.removeEventListener('error', onFail);
    };
  }, [onEnded, onProgress, videoRef]);

  useEffect(() => {
    const onChange = () => setFullscreen(document.fullscreenElement !== null);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const enter = () => setPip(true);
    const leave = () => setPip(false);
    video.addEventListener('enterpictureinpicture', enter);
    video.addEventListener('leavepictureinpicture', leave);
    return () => {
      video.removeEventListener('enterpictureinpicture', enter);
      video.removeEventListener('leavepictureinpicture', leave);
    };
  }, [videoRef]);

  // ---- commands -------------------------------------------------------------
  const toggle = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      wantPlay.current = true;
      void video.play().catch((cause: unknown) => {
        // AbortError is a source arriving or changing under a pending play,
        // not a refusal; the attach handler above picks it up. Only the
        // autoplay policy's NotAllowedError is worth a word.
        if (cause instanceof DOMException && cause.name === 'AbortError') return;
        wantPlay.current = false;
        setError('Your browser blocked playback. Press play again.');
      });
    } else {
      wantPlay.current = false;
      video.pause();
    }
  }, [videoRef]);

  const seekBy = useCallback(
    (delta: number) => {
      const video = videoRef.current;
      if (!video) return;
      video.currentTime = Math.max(0, Math.min(video.duration || 0, video.currentTime + delta));
    },
    [videoRef],
  );

  const seekToFraction = useCallback(
    (fraction: number) => {
      const video = videoRef.current;
      if (!video || !Number.isFinite(video.duration)) return;
      video.currentTime = video.duration * fraction;
    },
    [videoRef],
  );

  const setSpeed = useCallback(
    (value: number) => {
      const video = videoRef.current;
      if (video) video.playbackRate = value;
    },
    [videoRef],
  );

  /** Used by the reaction markers, and clamped so a stale marker cannot seek past the end. */
  const seekTo = useCallback(
    (seconds: number) => {
      const video = videoRef.current;
      if (!video) return;
      const limit = Number.isFinite(video.duration) ? video.duration : seconds;
      video.currentTime = Math.max(0, Math.min(limit, seconds));
      setCurrentTime(video.currentTime);
    },
    [videoRef],
  );

  const toggleFullscreen = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => {});
    } else {
      void container.requestFullscreen().catch(() => {
        // iPhone Safari only fullscreens the video element itself.
        const video = videoRef.current as (HTMLVideoElement & {
          webkitEnterFullscreen?: () => void;
        }) | null;
        video?.webkitEnterFullscreen?.();
      });
    }
  }, [videoRef]);

  // Picture in picture: the standard API where it exists, Safari's own
  // where it does not, and no button at all where neither does.
  const pipSupported =
    typeof document !== 'undefined' &&
    ('pictureInPictureEnabled' in document
      ? document.pictureInPictureEnabled
      : typeof (videoRef.current as { webkitSetPresentationMode?: unknown } | null)?.webkitSetPresentationMode === 'function');
  const togglePip = useCallback(() => {
    const video = videoRef.current as
      | (HTMLVideoElement & { webkitSetPresentationMode?: (mode: string) => void; webkitPresentationMode?: string })
      | null;
    if (!video) return;
    if (document.pictureInPictureElement) {
      void document.exitPictureInPicture().catch(() => {});
    } else if (typeof video.requestPictureInPicture === 'function') {
      void video.requestPictureInPicture().catch(() => {});
    } else if (video.webkitSetPresentationMode) {
      video.webkitSetPresentationMode(video.webkitPresentationMode === 'picture-in-picture' ? 'inline' : 'picture-in-picture');
    }
  }, [videoRef]);

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (video) video.muted = !video.muted;
  }, [videoRef]);

  useHotkeys({
    space: toggle,
    k: toggle,
    arrowleft: () => seekBy(-5),
    arrowright: () => seekBy(5),
    j: () => seekBy(-10),
    l: () => seekBy(10),
    m: toggleMute,
    f: toggleFullscreen,
    t: () => onTheaterToggle?.(),
    i: togglePip,
    '0': () => seekToFraction(0),
    '1': () => seekToFraction(0.1),
    '2': () => seekToFraction(0.2),
    '3': () => seekToFraction(0.3),
    '4': () => seekToFraction(0.4),
    '5': () => seekToFraction(0.5),
    '6': () => seekToFraction(0.6),
    '7': () => seekToFraction(0.7),
    '8': () => seekToFraction(0.8),
    '9': () => seekToFraction(0.9),
  });

  // ---- chrome auto-hide -----------------------------------------------------
  const wake = useCallback(() => {
    setShowControls(true);
    if (hideTimerRef.current !== null) window.clearTimeout(hideTimerRef.current);
    hideTimerRef.current = window.setTimeout(() => {
      if (videoRef.current && !videoRef.current.paused) setShowControls(false);
    }, 2600);
  }, [videoRef]);

  useEffect(() => {
    return () => {
      if (hideTimerRef.current !== null) window.clearTimeout(hideTimerRef.current);
    };
  }, []);

  const onScrub = (event: ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = Number(event.target.value);
    setCurrentTime(Number(event.target.value));
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
  const bufferedPercent = duration > 0 ? (buffered / duration) * 100 : 0;

  return (
    <div className="space-y-3">
      <div
        ref={containerRef}
        onMouseMove={wake}
        onTouchStart={wake}
        className="group relative overflow-hidden rounded-md border border-hairline bg-black"
      >
        <video
          ref={videoRef}
          playsInline
          preload="metadata"
          poster={playback.posterUrl ?? undefined}
          onClick={toggle}
          className="aspect-video w-full cursor-pointer bg-black"
          crossOrigin={playback.captionsUrl ? 'anonymous' : undefined}
        >
          {playback.captionsUrl ? (
            <track
              kind="captions"
              srcLang="en"
              label="English"
              src={playback.captionsUrl}
            />
          ) : null}
        </video>

        {!playing ? (
          <button
            type="button"
            onClick={toggle}
            aria-label={`Play ${title}`}
            className="absolute inset-0 flex items-center justify-center bg-slate-950/25 transition-colors duration-120 hover:bg-slate-950/35"
          >
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-950/80 text-chalk">
              <PlayIcon size={26} />
            </span>
          </button>
        ) : null}

        <div
          className={cx(
            'absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/95 to-transparent px-3 pb-2.5 pt-8 transition-opacity duration-200',
            showControls || !playing ? 'opacity-100' : 'pointer-events-none opacity-0',
          )}
        >
          {/* Above the track, never over it: a marker sitting on the scrub
              control would swallow drags that begin on it. */}
          <ReactionMarkers
            reactions={reactions}
            durationSec={duration}
            onSeek={seekTo}
            className="mx-0 mb-1 h-[18px]"
          />

          <div className="relative mb-2 h-1.5">
            <div className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-chalk/20" />
            <div
              className="absolute left-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-chalk/30"
              style={{ width: `${bufferedPercent}%` }}
            />
            <div
              className="absolute left-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-chalk"
              style={{ width: `${progressPercent}%` }}
            />
            <input
              type="range"
              min={0}
              max={duration || 0}
              step={0.1}
              value={currentTime}
              onChange={onScrub}
              aria-label="Seek"
              className="absolute inset-0 h-full w-full cursor-pointer appearance-none bg-transparent opacity-0"
            />
          </div>

          {/* Three groups: transport on the left, the clock dead centre,
              display controls on the right. The clock is positioned, not
              flexed, so it stays centred whatever the other two weigh. */}
          <div className="relative flex items-center justify-between gap-2">
            <div className="flex items-center gap-1">
              <ControlButton onClick={toggle} label={playing ? 'Pause' : 'Play'}>
                {playing ? <PauseIcon size={20} /> : <PlayIcon size={20} />}
              </ControlButton>
              <ControlButton onClick={() => seekBy(-5)} label="Back 5 seconds">
                <SkipBackIcon size={20} />
              </ControlButton>
              <ControlButton onClick={() => seekBy(5)} label="Forward 5 seconds">
                <SkipForwardIcon size={20} />
              </ControlButton>
              <div className="group/volume flex items-center">
                <ControlButton onClick={toggleMute} label={muted ? 'Unmute' : 'Mute'}>
                  {muted || volume === 0 ? <VolumeOffIcon size={20} /> : <VolumeIcon size={20} />}
                </ControlButton>
                <label className="hidden w-0 items-center overflow-hidden transition-[width] duration-200 group-hover/volume:w-20 group-focus-within/volume:w-20 sm:flex">
                  <span className="sr-only">Volume</span>
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.05}
                    value={muted ? 0 : volume}
                    onChange={(event) => {
                      const video = videoRef.current;
                      if (!video) return;
                      video.volume = Number(event.target.value);
                      video.muted = Number(event.target.value) === 0;
                    }}
                    className="ml-1 h-1 w-16 cursor-pointer appearance-none rounded-full bg-chalk/30 accent-chalk"
                  />
                </label>
              </div>
            </div>

            <span
              className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap font-mono text-caption tnum text-chalk"
              data-timecode
            >
              {timecode(currentTime)} <span className="text-chalk-faint">/ {timecode(duration)}</span>
            </span>

            <div className="flex items-center gap-1">
              {playback.captionsUrl ? (
                <button
                  type="button"
                  onClick={toggleCaptions}
                  aria-label={captions ? 'Hide captions' : 'Show captions'}
                  aria-pressed={captions}
                  className={`mr-1 rounded-xs border px-1.5 py-0.5 font-mono text-micro ${
                    captions ? 'border-chalk bg-chalk text-slate-950' : 'border-chalk/20 text-chalk'
                  }`}
                >
                  CC
                </button>
              ) : null}

              <select
                value={rate}
                onChange={(event) => {
                  setSpeed(Number(event.target.value));
                  onSpeedChange?.(Number(event.target.value));
                }}
                aria-label="Playback speed"
                className="mr-1 h-7 rounded-xs border border-chalk/20 bg-slate-950/70 px-1.5 font-mono text-micro tnum text-chalk"
              >
                {SPEEDS.map((speed) => (
                  <option key={speed} value={speed}>
                    {speed}×
                  </option>
                ))}
              </select>

              {onTheaterToggle ? (
                <ControlButton
                  onClick={onTheaterToggle}
                  label={theater ? 'Default view' : 'Theater mode'}
                  pressed={theater}
                  className="hidden lg:inline-flex"
                >
                  <TheaterIcon size={20} />
                </ControlButton>
              ) : null}
              {pipSupported ? (
                <ControlButton onClick={togglePip} label={pip ? 'Leave picture in picture' : 'Picture in picture'} pressed={pip}>
                  <PipIcon size={20} />
                </ControlButton>
              ) : null}
              <ControlButton onClick={toggleFullscreen} label={fullscreen ? 'Exit full screen' : 'Full screen'}>
                {fullscreen ? <ExitFullscreenIcon size={20} /> : <FullscreenIcon size={20} />}
              </ControlButton>
            </div>
          </div>
        </div>

        <ReactionPops
          reactions={reactions}
          currentTime={currentTime}
          className="absolute bottom-20 right-3 z-10"
        />
      </div>

      {error ? <Banner tone="danger" title="Playback problem">{error}</Banner> : null}

    </div>
  );
}

/** One control bar button: a square hit area, the icon in the middle. */
function ControlButton({
  onClick,
  label,
  pressed,
  className,
  children,
}: {
  onClick: () => void;
  label: string;
  pressed?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      className={cx(
        'inline-flex h-9 w-9 items-center justify-center rounded text-chalk transition-colors duration-120 hover:bg-chalk/10',
        pressed && 'bg-chalk/15',
        className,
      )}
    >
      {children}
    </button>
  );
}
